/**
 * Утилиты для определения уровня навигации в Wiki.
 * Логика скопирована с WikiAppSidebar - итерируем иерархию и проверяем наличие дочерних элементов.
 */

import { WikiUrlParams } from './wikiUrlParser';
import type { WikiNewCategory } from '../types';
import { resolveApiSource } from './wikiSourceResolver';

export interface NavigationLevel {
  showCards: boolean;
  hasChildren: boolean;
}

/**
 * Определяет нужно ли показывать карточки или список предметов.
 * Логика как в sidebar: если есть дочерние элементы - показываем карточки, иначе - список.
 */
export function getNavigationLevel(
  urlParams: WikiUrlParams | null,
  categories: WikiNewCategory[]
): NavigationLevel {
  // Нет категорий - ждём загрузки, показываем карточки (пустые)
  if (categories.length === 0) {
    return { showCards: true, hasChildren: false };
  }

  // Корень Wiki (/wiki) - показываем карточки всех категорий
  if (!urlParams) {
    return { showCards: true, hasChildren: categories.length > 0 };
  }

  const { source, subcategoryId, typeId, subtypeId, raceId, skillTypeId, classId, professionId } = urlParams;

  // Ищем категорию по resolved API source (как sidebar строит URL)
  let category: WikiNewCategory | undefined;
  for (const cat of categories) {
    // Проверяем resolved API source категории
    if (resolveApiSource([cat.source]) === source) {
      category = cat;
      break;
    }
    // Проверяем resolved API source подкатегорий
    for (const subCat of cat.subCategories || []) {
      if (resolveApiSource([cat.source, subCat.source]) === source) {
        category = cat;
        break;
      }
      // Проверяем типы подкатегорий
      for (const type of subCat.types || []) {
        if (resolveApiSource([cat.source, subCat.source, type.source]) === source) {
          category = cat;
          break;
        }
      }
      if (category) break;
    }
    if (category) break;
  }

  if (!category) {
    // Категория не найдена - показываем список (fallback)
    return { showCards: false, hasChildren: false };
  }

  // === SKILLS иерархия (races -> skillTypes -> classes -> professions) ===
  if (category.races && category.races.length > 0) {
    // Профессия выбрана - листовой узел
    if (professionId) {
      return { showCards: false, hasChildren: false };
    }

    // Класс выбран - проверяем есть ли профессии
    if (classId && skillTypeId && raceId) {
      const race = category.races.find(r => r.id === raceId);
      const skillType = race?.skillTypes?.find(st => st.id === skillTypeId);
      const classItem = skillType?.classes?.find(c => c.id === classId);
      const hasProfessions = !!(classItem?.professions && classItem.professions.length > 0);
      return { showCards: hasProfessions, hasChildren: hasProfessions };
    }

    // Тип скилла выбран - проверяем есть ли классы
    if (skillTypeId && raceId) {
      const race = category.races.find(r => r.id === raceId);
      const skillType = race?.skillTypes?.find(st => st.id === skillTypeId);
      const hasClasses = !!(skillType?.classes && skillType.classes.length > 0);
      return { showCards: hasClasses, hasChildren: hasClasses };
    }

    // Раса выбрана - проверяем есть ли типы скиллов
    if (raceId) {
      const race = category.races.find(r => r.id === raceId);
      const hasSkillTypes = !!(race?.skillTypes && race.skillTypes.length > 0);
      return { showCards: hasSkillTypes, hasChildren: hasSkillTypes };
    }

    // Только категория - есть расы
    return { showCards: true, hasChildren: true };
  }

  // === Стандартная иерархия (subCategories -> types -> subTypes) ===

  // Подтип выбран - листовой узел
  if (subtypeId) {
    return { showCards: false, hasChildren: false };
  }

  // Тип выбран - проверяем есть ли подтипы
  if (typeId && subcategoryId) {
    const subCategory = category.subCategories?.find(sc => sc.id === subcategoryId);
    const type = subCategory?.types?.find(t => t.id === typeId);
    const hasSubtypes = !!(type?.subTypes && type.subTypes.length > 0);
    return { showCards: hasSubtypes, hasChildren: hasSubtypes };
  }

  // Подкатегория выбрана - проверяем есть ли типы (как в sidebar: if (subCategory.types))
  if (subcategoryId) {
    const subCategory = category.subCategories?.find(sc => sc.id === subcategoryId);
    const hasTypes = !!(subCategory?.types && subCategory.types.length > 0);
    return { showCards: hasTypes, hasChildren: hasTypes };
  }

  // Только категория - проверяем есть ли подкатегории
  const hasSubcategories = !!(category.subCategories && category.subCategories.length > 0);
  return { showCards: hasSubcategories, hasChildren: hasSubcategories };
}
